import { CircleGeometry, Mesh, MeshBasicMaterial, Shape, ShapeGeometry } from 'three';
import HighlightedLine from './highlightedLine';
import { pixelRatio2d } from '@/editor/math/const';
import {
  ENTITY_PRIORITY,
  ENTITY_RENDER_ORDER,
  column2dColor,
  hoverOutlineColor,
  columnSelected2dColor,
  virtual2dWallColor
} from '@/editor/constant/entity';
import { globalStores } from '@/editor/stores';
import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { EContainerType, EEntityStatus } from '@/editor/types/entity';
import { getColumnBaseOutline } from '@/editor/utils/editStructUtils';
import { EEntityType } from '@/editor/types';
import BaseModel2d from './BaseModel2d';

export class ColumnModel extends BaseModel2d {
  private _columnMesh: Mesh;
  private _shapeLines: HighlightedLine[];
  private _status: EEntityStatus;
  private _selected: boolean = false;

  constructor(columnData: IColumnData & { status: EEntityStatus }) {
    super();
    this._status = columnData.status;
    this._shapeLines = [];
    this._columnMesh = new Mesh(this.createColumnEntGeometry(columnData), this.getMaterial());
    // this._columnMesh.renderOrder = ENTITY_RENDER_ORDER.column;

    this._modelGroup.add(this._columnMesh);

    this._modelGroup.userData.type = EEntityType.Column;
    this._modelGroup.userData.entityId = columnData.id;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.column;
    this._modelGroup.userData.containerType = EContainerType.entity;

    this.update({ ...columnData, selected: false });

    this.setDisplayOrder(ENTITY_RENDER_ORDER.column);
    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
  }

  override setVisible(visible: boolean) {
    this._modelGroup.visible = visible;
  }

  override getVisible() {
    return this._modelGroup.visible;
  }

  private getMaterial() {
    const material =
      this._status === EEntityStatus.virtual
        ? new MeshBasicMaterial({
            color: virtual2dWallColor,
            transparent: true,
            opacity: 0.6
          })
        : new MeshBasicMaterial({
            color: this._selected ? columnSelected2dColor : column2dColor
          });

    return material;
  }

  private createColumnEntGeometry(columnData: IColumnData) {
    if (columnData.type === EColumnType.squareColumn) {
      const outline = getColumnBaseOutline(columnData, { x: 0, y: 0 });
      const heartShape = new Shape();
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        if (i === 0) {
          heartShape.moveTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
        }
        heartShape.lineTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
      }
      const geometry = new ShapeGeometry(heartShape);

      return geometry;
    } else {
      const circleGeometry = new CircleGeometry(columnData.radius ? columnData.radius / pixelRatio2d : 0, 64);

      return circleGeometry;
    }
  }

  private updateColumnOutline(columnData: IColumnData) {
    for (const shapeLine of this._shapeLines) {
      this._modelGroup.remove(shapeLine.highlightedLine);
      shapeLine.dispose();
    }
    this._shapeLines.length = 0;

    const outline = getColumnBaseOutline(columnData, { x: 0, y: 0 });
    if (columnData.type === EColumnType.squareColumn) {
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        const start = outline[i];
        const end = outline[(i + 1) % len];
        const highlightedLine = new HighlightedLine([start, end], ENTITY_RENDER_ORDER.columnOutline);

        // if (i === 3) {
        //   const highlightedLine1 = new HighlightedLine([start, end], 200);
        //   highlightedLine1.setHighlightedColor(0x00ff00, 1);
        //   this._modelGroup.add(highlightedLine1.highlightedLine);
        // }
        this._shapeLines.push(highlightedLine);
        this._modelGroup.add(highlightedLine.highlightedLine);
      }
    } else {
      if (!columnData.radius) {
        return;
      }

      const highlightedLine = new HighlightedLine(outline, ENTITY_RENDER_ORDER.columnOutline);
      this._shapeLines.push(highlightedLine);
      this._modelGroup.add(highlightedLine.highlightedLine);
    }
  }

  private updateColumnModel(columnData: IColumnData) {
    // 替换几何体（先释放旧的）
    const geometry = this.createColumnEntGeometry(columnData);
    if (geometry) {
      this._columnMesh.geometry.dispose();
      this._columnMesh.geometry = geometry;
    }
    this._columnMesh.material = this.getMaterial();
  }

  update(columnData: IColumnData & { selected: boolean; status: EEntityStatus }) {
    const { center, status } = columnData;
    this._modelGroup.position.setX(center.x / pixelRatio2d);
    this._modelGroup.position.setY(center.y / pixelRatio2d);
    this._modelGroup.rotation.z = columnData.rotation;

    this._selected = columnData.selected || false;
    this._status = status;

    this.updateColumnModel(columnData);
    this.updateColumnOutline(columnData);
    this.updateStatus();
  }

  private updateStatus() {
    let color = column2dColor;
    let lineWidth = 0.5;
    if (this._selected) {
      color = columnSelected2dColor;
      lineWidth = 1;
    } else {
      if (this._status === EEntityStatus.normal) {
        color = column2dColor;
      } else if (this._status === EEntityStatus.hover) {
        color = hoverOutlineColor;
        lineWidth = 1.5;
      }
    }
    for (const shapeLine of this._shapeLines) {
      shapeLine.setHighlightedColor(color, lineWidth);
      // shapeLine.highlightedLine.renderOrder =
      //   this._status === EEntityStatus.normal ? ENTITY_RENDER_ORDER.columnOutline : ENTITY_RENDER_ORDER.renderOrderTemp;

      shapeLine.setVisible(
        this._status === EEntityStatus.virtual || this._status === EEntityStatus.normal ? false : true
      );
    }
  }
}
